import { AxiosResponse } from 'axios';
import { FieldSelect } from 'components/redesign/FieldSelect';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import useMediaQuery from 'hooks/useMediaQuery';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { modalActions } from 'reduxStore/reducers/ModalSlice';
import { ordersActions } from 'reduxStore/reducers/OrdersSlice';
import { getInitialCustomerRepresentativeFilters } from 'reduxStore/reducers/OrdersSlice/selectors/getCustomerRepresentativeFilters';
import { AgentService } from 'services/AgentService';
import { Button } from 'ui/redesign/Button';
import { ButtonVariant } from 'ui/redesign/Button/constants';
import { OptionType } from 'ui/redesign/Select/types';
import { transformToSelectOptions } from 'utils/helpers';
import { IS_SMALL_DEVICE } from 'utils/settings';
import { defaultFilters, defaultMaxResultCount, defaultSkipCount } from './consts';
import { CustomerRepresentativeFiltersFormValues, InitiatorsDto } from './types';
import useDebounce from 'hooks/useDebounce';
import { DictionaryEntities } from 'services/AgentService/types';

export const CustomerRepresentativeFiltersForm = () => {
  const { mutateAsync: getInitiatorList, data } = useMutation<
    AxiosResponse<DictionaryEntities<InitiatorsDto>>,
    unknown,
    { nameTemplate: string; skipCount: number; maxResultCount: number }
  >({
    mutationKey: 'getInitiatorList',
    mutationFn: ({ nameTemplate, skipCount, maxResultCount }) =>
      AgentService.getInitiatorList({ nameTemplate, skipCount, maxResultCount }),
  });

  const getDebounceInitiatorList = useDebounce(getInitiatorList, 1000);

  const initiators = data?.data.items ?? [];

  const filters = useAppSelector(getInitialCustomerRepresentativeFilters);

  const [scrollMaxResultCount, setScrollMaxResultCount] = useState(defaultMaxResultCount);
  const [savedNameTemplate, setSavedNameTemplate] = useState('');

  const dispatch = useAppDispatch();
  const isSmall = useMediaQuery(IS_SMALL_DEVICE);
  const { control, handleSubmit, reset, getValues } =
    useForm<CustomerRepresentativeFiltersFormValues>({
      defaultValues: { initiators: filters.initiators },
    });

  const onCloseFilters = () => {
    dispatch(modalActions.closeModal());
  };

  const onResetFilter = () => {
    reset(defaultFilters);
  };

  const submitFiltersHandler = () => {
    const values = getValues();
    const initiators = values.initiators as OptionType<string>[] | undefined;
    dispatch(
      ordersActions.setCustomerRepresentativeFilters({
        initiators: initiators ?? [],
      })
    );
    dispatch(modalActions.closeModal());
  };

  useEffect(() => {
    getInitiatorList({
      nameTemplate: '',
      skipCount: defaultSkipCount,
      maxResultCount: defaultMaxResultCount,
    });
  }, []);

  // Маленький экран
  if (isSmall) {
    return (
      <div className="-mt-4 flex w-full flex-col">
        <div className="mb-4 flex w-full cursor-pointer items-center justify-center gap-2">
          <form className="flex h-full w-full flex-col p-4">
            <div className="flex grow flex-col gap-4">
              <div className="flex justify-between">
                <span className="title2-medium">Фильтр</span>
                <button className="mr-2 !text-primary-60" type="button" onClick={onResetFilter}>
                  Очистить
                </button>
              </div>
              <div>
                <FieldSelect
                  name="initiators"
                  control={control}
                  isMulti
                  variant="filter"
                  label="Инициатор оплаты"
                  isClearable
                  options={transformToSelectOptions(initiators, 'fullName', 'id')}
                  onInputChange={(newValue: string) => {
                    if (newValue.length < 3) {
                      return;
                    }
                    setSavedNameTemplate(newValue);
                    getDebounceInitiatorList({
                      nameTemplate: newValue,
                      skipCount: defaultSkipCount,
                      maxResultCount: defaultMaxResultCount,
                    });
                    setScrollMaxResultCount(defaultMaxResultCount);
                  }}
                  onBlur={() => {
                    getInitiatorList({
                      nameTemplate: '',
                      skipCount: defaultSkipCount,
                      maxResultCount: defaultMaxResultCount,
                    });
                    setScrollMaxResultCount(defaultMaxResultCount);
                    setSavedNameTemplate('');
                  }}
                  onMenuScrollToBottom={() => {
                    getInitiatorList({
                      nameTemplate: savedNameTemplate,
                      skipCount: defaultSkipCount,
                      maxResultCount: scrollMaxResultCount + defaultMaxResultCount,
                    });
                    setScrollMaxResultCount((count) => count + defaultMaxResultCount);
                  }}
                />
              </div>
            </div>
            <div className="fixed bottom-10 w-full pr-8">
              <Button
                className="mb-6 w-full max-w-[640px]"
                type="button"
                onClick={submitFiltersHandler}
              >
                ПРИМЕНИТЬ ФИЛЬТРЫ
              </Button>
              <Button
                variant={ButtonVariant.OUTLINE}
                className="btn-ghost w-full max-w-[640px] self-center"
                type="button"
                onClick={onCloseFilters}
              >
                ЗАКРЫТЬ
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  } else {
    return (
      <form className="relative flex flex-col gap-6">
        <div className="flex grow flex-col gap-4">
          <div className="flex justify-between">
            <span className="title2-medium text-text-100">Фильтр</span>
            <button className="mr-2 !text-primary-60" type="button" onClick={onResetFilter}>
              Очистить
            </button>
          </div>
          <div>
            <FieldSelect
              name="initiators"
              control={control}
              isMulti
              variant="filter"
              label="Инициатор оплаты"
              isClearable
              options={transformToSelectOptions(initiators, 'fullName', 'id')}
              onInputChange={(newValue: string) => {
                if (newValue.length < 1) {
                  return;
                }
                setSavedNameTemplate(newValue);
                getDebounceInitiatorList({
                  nameTemplate: newValue,
                  skipCount: defaultSkipCount,
                  maxResultCount: defaultMaxResultCount,
                });
                setScrollMaxResultCount(defaultMaxResultCount);
              }}
              onBlur={() => {
                getInitiatorList({
                  nameTemplate: '',
                  skipCount: defaultSkipCount,
                  maxResultCount: defaultMaxResultCount,
                });
                setScrollMaxResultCount(defaultMaxResultCount);
                setSavedNameTemplate('');
              }}
              onMenuScrollToBottom={() => {
                getInitiatorList({
                  nameTemplate: savedNameTemplate,
                  skipCount: defaultSkipCount,
                  maxResultCount: scrollMaxResultCount + defaultMaxResultCount,
                });
                setScrollMaxResultCount((count) => count + defaultMaxResultCount);
              }}
            />
          </div>
        </div>
        <Button className="ml-auto" type="button" onClick={submitFiltersHandler}>
          ПРИМЕНИТЬ
        </Button>
      </form>
    );
  }
};
